import { TIMEZONE_MS, UUID_NAMESPACE } from '@/constants';
import { useFetchAxios, useTables } from '@/hooks';
import { getOnStorage, setOnStorage } from '@/shared';
import { useDbFunctions } from '@/talons';
import { LogEvents } from '@/types';
import { v5 as uuidv5 } from 'uuid';
import pacote from '../../../package.json';

const TOTAL_EVENTO = 0;
const DISABLE_LOCAL_EVENTS = false;
const ENABLE_ALL_EVENTS = false;

interface LogEventsPartial
  extends Omit<
    LogEvents,
    | 'id'
    | 'uuid'
    | 'eventUUID'
    | 'origemEvento'
    | 'dtEvento'
    | 'origemDispositivo'
    | 'rotaPagina'
    | 'codRca'
    | 'codLoja'
    | 'codCliente'
    | 'idCarrinho'
    | 'tipoUsuario'
    | 'stEvento'
    | 'codProduto'
  > {
  tipoUsuario?: string;
  codRca?: number;
  codProduto?: number;
  codLoja?: number;
  codCliente?: number;
  idCarrinho?: number;
  stEvento?: number;
}

export const getDateTimeMS = (): string => new Date().toLocaleString('sv', { timeZone: TIMEZONE_MS }).replace(' ', 'T');

const MOBILE_UA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

let cachedPlatform: string | null = null;

const getPlatform = (): string => {
  if (cachedPlatform !== null) return cachedPlatform;
  const uaMobile = MOBILE_UA.test(navigator.userAgent);
  const hintMobile = navigator.userAgentData?.mobile === true;
  const isMobile = uaMobile || hintMobile;
  cachedPlatform = isMobile ? 'MOBILE' : 'DESKTOP';
  return cachedPlatform;
};

const getStorageInfos = async () => {
  const nav = navigator as any;
  const storage = (await nav.storage.estimate()) as StorageEstimate;
  if (!storage) return {};
  return {
    // Espaço total disponível para armazenamento da origem, em MB (storage.quota convertido de bytes).
    quotaMB: Math.round(storage.quota! / 1024 / 1024).toFixed(2),
    usageMB: Math.round(storage.usage! / 1024 / 1024).toFixed(2),
  };
};

export const collectPWAMetrics = async () => {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    hardwareConcurrency?: number;
    connection?: { effectiveType?: string };
  };
  const storage = await getStorageInfos();
  const performance = window.performance as any;

  return {
    device: {
      // RAM do aparelho em GB (navigator.deviceMemory). Pode ser undefined em alguns browsers; nesse caso vira null.
      memoryGB: nav.deviceMemory || null,
      // Número de núcleos lógicos da CPU (navigator.hardwareConcurrency). Também pode ser undefined e virar null.
      cpuCores: nav.hardwareConcurrency || null,
    },
    storage,
    network: nav.connection && {
      // Tipo efetivo de conexão (effectiveType): ex. "4g", "3g", "slow-2g".
      type: nav.connection.effectiveType,
    },
    runtime: {
      // Memória de heap JS usada no momento, em MB (performance.memory.usedJSHeapSize). Só em Chrome/Chromium; em outros browsers fica null.
      heapMB: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) : null,
      // Tempo em ms desde o início do carregamento da página (performance.now()). Útil para medir duração de operações.
      time: performance.now(),
    },
    environment: {
      // String completa do User-Agent do navegador.
      userAgent: nav.userAgent,
      // Plataforma do SO reportada pelo browser (navigator.platform), ex. "MacIntel", "Win32".
      platform: nav.platform,
      // Se o app está rodando como PWA instalado (janela sem barra do browser). true quando (display-mode: standalone) dá match; false quando está na aba do browser.
      standalone: window.matchMedia('(display-mode: standalone)').matches,
    },
  };
};

const generateUUID = (codRca: number) => {
  const uuid = getOnStorage('uuid');
  if (uuid) return uuid;

  const currentTime = Date.now();
  const dispositivo = getPlatform();
  const userAgent = navigator.userAgent;
  const name = `${dispositivo}-${userAgent}-${codRca}-${currentTime}`;
  const newUuid = uuidv5(name, UUID_NAMESPACE);

  setOnStorage('uuid', newUuid);
  return newUuid;
};

export const useEvents = (dbFunctions: ReturnType<typeof useDbFunctions>) => {
  const { tables } = useTables();
  const { axiosCtx } = useFetchAxios();

  const registerEvent = async (logEvents: LogEventsPartial) => {
    if (process.env.NODE_ENV === 'development' && DISABLE_LOCAL_EVENTS) {
      return;
    }

    if (!dbFunctions || !dbFunctions.getOnDB || !dbFunctions.setOnDB) return;

    const codRca = Number(logEvents.codRca || getOnStorage('cod_pessoa') || 0);
    if (!codRca) return;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    const { getOnDB, setOnDB } = dbFunctions;
    const orderNumbers = getOnStorage('order_numbers');
    const selectedClient = getOnStorage('selected_client');
    const offlineEvents = (await getOnDB('offline_events', undefined, undefined, true)) || [];

    const storage = await getStorageInfos();
    const tipoEvento = logEvents.tipoEvento || 'UNKNOWN';
    const stEvento = logEvents.stEvento ?? 1;
    const obsNetStatus = logEvents.tipoEvento === 'NETWORK_STATUS' ? '' : `(${navigator.onLine ? 'ON' : 'OFF'}) `;
    const obsMemory = tipoEvento !== 'NETWORK_STATUS' ? `(cota: ${~~storage.quotaMB!}MB, uso: ${~~storage.usageMB!}MB) ` : '';
    const obsEvento = `${obsNetStatus}[v${pacote.version}] ${obsMemory}${logEvents.obsEvento || 'UNKNOWN'}`.trim();
    const dtEvento = getDateTimeMS();

    const event: Partial<Omit<LogEvents, 'id'>> = {
      // 1: Sucesso, 0: Falha
      stEvento: stEvento,
      tipoEvento: tipoEvento,
      obsEvento: obsEvento,
      uuid: generateUUID(codRca),
      eventUUID: uuidv5(`${tipoEvento}-${dtEvento}`, UUID_NAMESPACE),

      origemEvento: 'PWA',
      dtEvento: dtEvento,
      origemDispositivo: getPlatform(),
      rotaPagina: window.location.hash.replace('#', ''),

      codProduto: logEvents.codProduto || 0,
      codRca: codRca,
      codLoja: Number(logEvents.codLoja || selectedClient?.COD_LOJA || 0),
      codCliente: Number(logEvents.codCliente || selectedClient?.COD_CLIENTE || 0),
      idCarrinho: logEvents.idCarrinho || orderNumbers?.[selectedClient?.COD_CLIENTE || '']?.orderNumber || null,
      tipoUsuario: logEvents.tipoUsuario || getOnStorage('rca_usuario')?.tipoUsuario || 'RCA',
    };

    const paramsAWS = tables?.current?.parametros_aws;

    const currentSentEvents = getOnStorage('sent_events') || {};
    const body = [...offlineEvents, event].reduce((acc: LogEvents[], evnt, index) => {
      // Verifica se o evento está duplicado no IndexDB
      const isDuplicatedInDB = acc.find((e) => e.eventUUID === evnt.eventUUID);
      if (isDuplicatedInDB) return acc;

      // Verifica se o evento já foi enviado (localStorage)
      const isDuplicatedInLocalStorage = currentSentEvents[evnt.eventUUID as string];
      if (isDuplicatedInLocalStorage) return acc;

      // Verifica se o evento está com status 0 (Falha) - Falhas sempre são enviadas
      if (ENABLE_ALL_EVENTS || evnt.stEvento === 0 || !paramsAWS) {
        acc.push({ ...evnt, id: index + 1 } as LogEvents)
        return acc;
      };

      const hasBeenAllowedEvent = paramsAWS.find((evt) => evt.DESC_PARAMETRO?.toUpperCase() === evnt.tipoEvento!.toUpperCase() || evt.DESC_PARAMETRO?.toLowerCase() === 'todos');
      const eventValues = Array.isArray(hasBeenAllowedEvent?.VALOR_PARAMETRO) ? hasBeenAllowedEvent?.VALOR_PARAMETRO.join('') : hasBeenAllowedEvent?.VALOR_PARAMETRO;
      const hasBeenAllowedRCA = eventValues?.includes(`${evnt.codRca}`) || eventValues?.toLowerCase()?.includes('todos');

      // Verifica se o evento foi permitido pelo parâmetro AWS
      if (hasBeenAllowedEvent && hasBeenAllowedRCA) {
        acc.push({ ...evnt, id: index + 1 } as LogEvents);
      }
      return acc
    }, []);

    // Salva os eventos no IndexDB
    const sentEvents: Record<string, boolean> = getOnStorage('sent_events') || {};
    for (const ev of body) {
      sentEvents[ev.eventUUID] = true;
    }
    setOnStorage('sent_events', sentEvents);
    await setOnDB(body as LogEvents[], 'offline_events', true);

    if (navigator.onLine && axiosCtx && (body.length > TOTAL_EVENTO || stEvento === 0)) {
      const joinBody = body.map((event) => `${event.tipoEvento}-${event.stEvento}`).join(',');
      return axiosCtx
        .post(`/api-proxy?evts=${joinBody}`, {
          body: body,
          method: 'POST',
          endpoint: '/v1/Insere-eventos',
        }, { disableInterceptor: true })
        .then(async (response) => {
          if (response.data?.resposta?.error_code != 200) {
            const list = (await getOnDB('offline_events', undefined, undefined, true)) || [];
            const filteredBody = body.filter((evnt) => !list.find((e) => e.eventUUID === evnt.eventUUID));
            const mergedList = [...list, ...filteredBody];
            await setOnDB(mergedList, 'offline_events', true);
            return response
          };
          
          const listSentEvents = getOnStorage('sent_events') || {};
          for (const ev of body) {
            delete listSentEvents[ev.eventUUID];
          }

          setOnStorage('sent_events', listSentEvents);
          await setOnDB([], 'offline_events', true);
          return response;
        })
        .catch(async (error) => {
          const listSentEvents = getOnStorage('sent_events') || {};
          for (const ev of body) {
            delete listSentEvents[ev.eventUUID];
          }

          setOnStorage('sent_events', listSentEvents);
          const list = (await getOnDB('offline_events', undefined, undefined, true)) || [];
          const filteredBody = body.filter((evnt) => !list.find((e) => e.eventUUID === evnt.eventUUID));
          const mergedList = [...list, ...filteredBody];
          await setOnDB(mergedList, 'offline_events', true);
          return error;
        });
    } else {
      const listSentEvents = getOnStorage('sent_events') || {};
      for (const ev of body) {
        delete listSentEvents[ev.eventUUID];
      }

      setOnStorage('sent_events', listSentEvents);
      const list = (await getOnDB('offline_events', undefined, undefined, true)) || [];
      const filteredBody = body.filter((evnt) => !list.find((e) => e.eventUUID === evnt.eventUUID));
      const mergedList = [...list, ...filteredBody];
      await setOnDB(mergedList, 'offline_events', true);
      return Promise.resolve(true);
    }
  };

  const registerVersionEvent = async (codRca?: number) => {
    const currentVersion = getOnStorage('system_version');
    if (currentVersion === pacote.version) return;

    setOnStorage('system_version', pacote.version);
    await registerEvent({
      tipoEvento: 'VERSAO',
      obsEvento: `Versão do PWA: ${pacote.version} | RCA: ${codRca}`,
      codRca: Number(codRca || getOnStorage('cod_pessoa') || 0),
    });
  };

  return { registerEvent, registerVersionEvent };
};
